import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Switch,
} from '@mui/material';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import Iconify from '../common/iconify.component';
import validationRules from '../../utils/validations.util';
import RulesetCondition from './condition.component';
import useAccess from '../../hooks/access.hook';
import Rules from '../form-elements/rules.component';
import AddEditRules from './add-edit-rules.component';
import {
  createRuleset,
  getRulesetById,
  updateRuleset,
} from '../../services/ruleset.service';
import Loading from '../table-elements/loading.component';
import ConfirmPopup from '../common/confirm-popup.component';

const AddEditScheduleRulesetRules = ({
  title,
  channelContentScheduleInfoId,
  dataId,
  rulesetType,
  onSave,
  onCancel,
  showSnackbarMessage,
  isHeroScheduling,
}) => {
  const { hasOperationAccess } = useAccess();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [createNewRule, setCreateNewRule] = useState(false);
  const [showConfirmDailog, setShowConfirmDailog] = useState(false);
  const [reloadRules, setReloadRules] = useState(false);
  const selectedRulesetDetail = useRef(null);
  const isSkippedContentRule = useRef(0);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, dirtyFields },
  } = useForm({
    defaultValues: {},
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rules',
  });
  const watchContentType = watch('type');
  const isDynamic = useWatch({ control, name: 'isDynamicVideosCount' });
  const [loading, setLoading] = useState(true);

  const saveData = (data) => {
    setFormSubmitted(true);
    const rulesetPayload = {
      name: data.title,
      rulesCondition: data.condition || 'OR',
      rulesetType,
      rules: (data.rules || []).map((item) => item.id?.id && item.id.id),
      editConfirmed: !!showConfirmDailog,
    };
    rulesetPayload.type =
      data.type === 'MOVIES' ? data.type : data.seriesContentType;
    rulesetPayload.numberOfDaysInAdvance = !(
      data.seriesContentType === 'SERIES_SEASON' && data.type === 'SERIES'
    )
      ? Number(data.scheduleXDaysAdvance)
      : null;
    rulesetPayload.numberOfContentPerWeek = Number(data.videosCountPerWeek);
    rulesetPayload.numberOfDaysBetweenContent =
      data.seriesContentType === 'SERIES_SEASON' && data.type === 'SERIES'
        ? Number(data.timeBeetweenSeason)
        : null;
    rulesetPayload.channelContentScheduleInfoId = channelContentScheduleInfoId;
    rulesetPayload.dynamicContentPerWeek = !!isDynamic;
    if (rulesetPayload.dynamicContentPerWeek) {
      rulesetPayload.numberOfContentPerWeek = 0;
    }
    if (dataId) {
      rulesetPayload.rulesetId = dataId;
    }

    if (!dataId) {
      createRuleset(rulesetPayload)
        .then(() => {
          onSave();
          setFormSubmitted(false);
        })
        .catch((err) => {
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';
          showSnackbarMessage(message, 'error');
          setFormSubmitted(false);
        });
    } else {
      updateRuleset(rulesetPayload)
        .then(() => {
          onSave();
          setFormSubmitted(false);
        })
        .catch((err) => {
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';
          showSnackbarMessage(message, 'error');
          setFormSubmitted(false);
        });
    }
  };

  const onSubmit = (data) => {
    if (
      Object.keys(dirtyFields).filter((item) => item !== 'title').length > 0 &&
      dataId
    ) {
      setShowConfirmDailog(true);
    } else {
      saveData(data);
    }
  };

  const handleRuleOpen = () => {
    setCreateNewRule(true);
  };
  const handleRuleCancel = () => {
    setCreateNewRule(false);
  };
  const handleRuleSuccess = (_, newRule) => {
    setCreateNewRule(false);
    append({
      id: newRule,
      rule: newRule.title,
    });
    setReloadRules((prev) => !prev);
  };

  useEffect(() => {
    if (!dataId) {
      setValue('title', '');
      setValue('condition', 'AND');
      setValue('type', 'MOVIES');
      setValue('seriesContentType', 'SERIES_EPISODES');
      setValue('videosCountPerWeek', '0');
      setValue('scheduleXDaysAdvance', '30');
      setValue('timeBeetweenSeason', '');
      setValue('rules', []);
      setValue('isDynamicVideosCount', false);
      // setShowViewMode(false);
      setLoading(false);
      return;
    }

    getRulesetById(dataId)
      .then((res) => {
        selectedRulesetDetail.current = res.data;
        setValue('title', res.data?.name || '');
        setValue('condition', res.data?.rulesCondition || 'AND');
        setValue(
          'type',
          res.data?.type !== 'MOVIES' ? 'SERIES' : 'MOVIES' || 'MOVIES'
        );
        setValue(
          'seriesContentType',
          res.data?.type !== 'MOVIES' ? res.data?.type : 'SERIES_EPISODES'
        );
        setValue('videosCountPerWeek', res.data?.numberOfContentPerWeek || '0');
        setValue(
          'scheduleXDaysAdvance',
          res.data?.numberOfDaysInAdvance || '0'
        );
        setValue(
          'timeBeetweenSeason',
          res.data?.numberOfDaysBetweenContent || ''
        );
        setValue(
          'isDynamicVideosCount',
          (res.data?.dynamicContentPerWeek || 0) === 1
        );
        setValue('rules', []);
        if (res.data?.rules?.length) {
          res.data?.rules.forEach((rule, index) => {
            setValue(`rules.${index}.id`, res.data?.rules[index]);
            setValue(`rules.${index}.rule`, rule.title);
          });
        }
        isSkippedContentRule.current = res.data?.isSkippedContentRule || 0;
        // setShowViewMode(true);
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        showSnackbarMessage(message, 'error');
      });
  }, [dataId]);

  return (
    <>
      <Dialog open onClose={onCancel} fullWidth maxWidth="lg">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {loading && <Loading />}

          {!loading && (
            <form
              id="add-edit-ruleset"
              onSubmit={handleSubmit(onSubmit)}
              className="w-full"
              style={{
                maxHeight: 'calc(100% - 75px)',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Grid container spacing={2} marginBottom={2}>
                <Grid item xs={12} sm={rulesetType === 'ASSIGNMENT' ? 6 : 4}>
                  <Controller
                    name="title"
                    control={control}
                    rules={validationRules.REQUIRED}
                    disabled={isSkippedContentRule.current === 1}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="filled"
                        label="Name"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
                {isSkippedContentRule.current === 0 && (
                  <Grid item xs={12} sm={rulesetType === 'ASSIGNMENT' ? 6 : 4}>
                    <Controller
                      name="condition"
                      control={control}
                      rules={validationRules.REQUIRED}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <RulesetCondition
                          name="condition"
                          id="ruleset"
                          defaultValue={value}
                          onChange={onChange}
                          label="Condition"
                          error={error?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
                {rulesetType !== 'ASSIGNMENT' && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <Box display="flex">
                        <Controller
                          name="isDynamicVideosCount"
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Switch {...field} checked={field.value} />
                              }
                              style={{ width: '400px' }}
                              label={
                                isDynamic
                                  ? 'Dynamic Videos Count Per Week'
                                  : 'Fixed Videos Count Per Week'
                              }
                              disabled={
                                watchContentType === 'SERIES' ||
                                isSkippedContentRule.current === 1
                              }
                            />
                          )}
                        />

                        <Controller
                          name="videosCountPerWeek"
                          control={control}
                          rules={validationRules.REQUIRED}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              variant="filled"
                              label={
                                watchContentType === 'SERIES' &&
                                watch('seriesContentType') === 'SERIES_SEASON'
                                  ? 'Seasons'
                                  : 'Videos'
                              }
                              type="number"
                              error={!!errors.videosCountPerWeek}
                              helperText={
                                errors?.videosCountPerWeek?.message || null
                              }
                              disabled={isDynamic}
                            />
                          )}
                        />
                      </Box>
                    </Grid>
                    {!(
                      watchContentType === 'SERIES' &&
                      watch('seriesContentType') === 'SERIES_SEASON'
                    ) && (
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="scheduleXDaysAdvance"
                          control={control}
                          rules={validationRules.REQUIRED}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              variant="filled"
                              label="Schedule X Days Advance"
                              type="number"
                              error={!!errors.scheduleXDaysAdvance}
                              helperText={
                                errors?.scheduleXDaysAdvance?.message || null
                              }
                            />
                          )}
                        />
                      </Grid>
                    )}
                    {watchContentType === 'SERIES' &&
                      watch('seriesContentType') === 'SERIES_SEASON' && (
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name="timeBeetweenSeason"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                value={field.value}
                                fullWidth
                                variant="filled"
                                label="Time Beetween Season"
                                type="number"
                                error={!!errors.timeBeetweenSeason}
                                helperText={
                                  errors?.timeBeetweenSeason?.message || null
                                }
                              />
                            )}
                          />
                        </Grid>
                      )}

                    {isSkippedContentRule.current === 0 &&
                      !isHeroScheduling && (
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name="type"
                            control={control}
                            rules={validationRules.REQUIRED}
                            render={({ field }) => (
                              <FormControl component="fieldset">
                                <p
                                  style={{
                                    marginBottom: '4px',
                                    marginTop: 0,
                                    fontWeight: 'bold',
                                  }}
                                >
                                  Select Content Type
                                </p>
                                <RadioGroup
                                  {...field}
                                  style={{ flexDirection: 'row' }}
                                  aria-label="type"
                                  name="type"
                                >
                                  <FormControlLabel
                                    value="MOVIES"
                                    control={<Radio />}
                                    label="Movies"
                                  />
                                  <FormControlLabel
                                    value="SERIES"
                                    control={<Radio />}
                                    label="Series"
                                  />
                                </RadioGroup>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      )}
                    {watchContentType === 'SERIES' && (
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="seriesContentType"
                          control={control}
                          rules={validationRules.REQUIRED}
                          render={({ field }) => (
                            <FormControl component="fieldset">
                              <p
                                style={{
                                  marginBottom: '4px',
                                  marginTop: 0,
                                  fontWeight: 'bold',
                                }}
                              >
                                Select Series Content Type
                              </p>
                              <RadioGroup
                                {...field}
                                style={{ flexDirection: 'row' }}
                                aria-label="type"
                                name="type"
                              >
                                <FormControlLabel
                                  value="SERIES_EPISODES"
                                  control={<Radio />}
                                  label="Episode"
                                />
                                <FormControlLabel
                                  value="SERIES_SEASON"
                                  control={<Radio />}
                                  label="Season"
                                />
                              </RadioGroup>
                            </FormControl>
                          )}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>

              {isSkippedContentRule.current === 0 && (
                <>
                  <Typography variant="subtitle1" marginTop={4}>
                    Content Filter Logic:
                  </Typography>
                  <Box
                    sx={{
                      border: '1px dashed #e3e3e3',
                      borderRadius: 2,
                      mt: 1,
                      p: 1,
                    }}
                  >
                    {fields.map((field1, index) => (
                      <Box>
                        <Grid
                          container
                          spacing={2}
                          sx={{
                            alignItems: 'center',
                            marginBlock: 2,
                          }}
                          justifyContent="space-between"
                          key={field1.id}
                        >
                          <Grid item xs={12} sm={4} sx={{ pt: 0 }}>
                            <FormControl fullWidth required>
                              <Controller
                                name={`rules.${index}.id`}
                                control={control}
                                rules={{ required: 'Please select a rule' }}
                                render={({ field: { onChange, value } }) => (
                                  <Rules
                                    id={`field-rule-${index}`}
                                    name={`field-rule-${index}`}
                                    label="Select Rule"
                                    defaultValue={value}
                                    onChange={(e) => {
                                      onChange(e);
                                      setValue(`rules.${index}.rule`, e?.title);
                                    }}
                                    reload={reloadRules}
                                    sx={{ width: '100%' }}
                                    error={
                                      errors?.rules?.[index]?.id?.message || ''
                                    }
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={4} sx={{ pt: 0 }}>
                            <FormControl fullWidth required>
                              <Controller
                                name={`rules.${index}.rule`}
                                control={control}
                                render={({ field: { value } }) => (
                                  <Typography variant="body1">
                                    {value || '-'}
                                  </Typography>
                                )}
                              />
                            </FormControl>
                          </Grid>

                          {hasOperationAccess(
                            'rule_based_scheduling',
                            'manage'
                          ) && (
                            <Grid item xs={12} sm={0.5} sx={{ pt: 0, pr: 0 }}>
                              {fields.length && (
                                <IconButton onClick={() => remove(index)}>
                                  <Iconify icon="material-symbols:delete-forever-outline-rounded" />
                                </IconButton>
                              )}
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ))}

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        py: fields.length === 0 ? 5 : 1,
                      }}
                    >
                      {fields.length === 0 && (
                        <Typography variant="body2" mb={1}>
                          Please add content filter conditions for this set.
                          Ruleset will only work if it has at least one
                          condition defined for filtering content.
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {hasOperationAccess(
                          'rule_based_scheduling',
                          'manage'
                        ) && (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => append({ id: null, rule: '' })}
                            sx={{
                              minWidth: 24,
                              padding: '2px 6px',
                              fontSize: '0.7rem',
                              width: 'fit-content',
                            }}
                          >
                            Choose from existing
                          </Button>
                        )}
                        <Typography variant="body2">or</Typography>
                        {(hasOperationAccess('rules_library', 'create') ||
                          hasOperationAccess('rules_library', 'edit')) && (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => handleRuleOpen()}
                            sx={{
                              minWidth: 24,
                              padding: '2px 6px',
                              fontSize: '0.7rem',
                              width: 'fit-content',
                            }}
                          >
                            Add New Condition
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="contained"
            color="inherit"
            disabled={formSubmitted}
            onClick={onCancel}
            sx={{ mr: 1 }}
          >
            Close
          </Button>
          {hasOperationAccess('rule_based_scheduling', 'manage') && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              form="add-edit-ruleset"
              type="submit"
              disabled={formSubmitted}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {showConfirmDailog && (
        <ConfirmPopup
          title="Update Ruleset"
          message={`Do you want to update ruleset data? This will ${
            rulesetType === 'ASSIGNMENT'
              ? 'purge all existing assignment'
              : 'purge future scheduling content'
          } of this ruleset.`}
          onCancel={() => setShowConfirmDailog(false)}
          onSuccess={() => saveData(getValues())}
          successButtonText="Proceed"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="confirm"
        />
      )}
      {createNewRule && (
        <AddEditRules
          title="Create New Rule"
          dataId={0}
          onCancel={handleRuleCancel}
          onSuccess={handleRuleSuccess}
        />
      )}
    </>
  );
};

AddEditScheduleRulesetRules.propTypes = {
  title: PropTypes.string.isRequired,
  channelContentScheduleInfoId: PropTypes.number.isRequired,
  dataId: PropTypes.number.isRequired,
  rulesetType: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  showSnackbarMessage: PropTypes.func.isRequired,
  isHeroScheduling: PropTypes.bool.isRequired,
};

export default AddEditScheduleRulesetRules;
